import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Star, Globe, Users, Award } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './LandingPage.css';
import hero_banner from '../../img/greatly-woman-with-bank-card.svg'
import hero_quote_section from '../../img/man-with-phone.svg'
import hero_phone_app_banner from '../../img/banner.svg'
import phone_app_banner from '../../img/phone-app-banner.svg'
import warranty from '../../img/warranty.svg'
import world_icon__features from '../../img/world.svg'
import reputation_icon__features from '../../img/REputation.svg'
/**
 * Главная страница лендинга Debt Agent
 * Объединяет все секции в одном компоненте
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [screenSize, setScreenSize] = useState('desktop');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Отслеживание размера экрана
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setScreenSize('mobile');
      } else if (width < 968) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Обработка перехода с якорем из других страниц
  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      // Небольшая задержка для загрузки страницы
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      // Очищаем state после использования
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const isMobile = screenSize === 'mobile';

  // Функции управления модальным окном
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const handleConfirm = () => {
    closeModal();
    navigate('/contact-form');
  };

  // Секции для навигации
  const sections = [
    { id: 'hero', label: 'Home' },
    { id: 'testimonial', label: 'About Us' },
    { id: 'legal-help', label: 'Services' },
    { id: 'success-stories', label: 'Success Stories' },
    { id: 'features', label: 'Features' }
  ];

  // Данные для банков
  const bankLogos = [
    { name: 'DISCOVER', src: '/img/icon-discover-logo-136-23 1.svg', alt: 'Логотип DISCOVER' },
    { name: 'Fannie Mae', src: '/img/Logo_R_Websites_rbg_nvy_242x72_trans 1.svg', alt: 'Логотип Fannie Mae' },
    { name: 'American Express', src: '/img/american-express-logo.svg', alt: 'Логотип American Express' },
    { name: 'Citi', src: '/img/sity-logo.svg', alt: 'Логотип Citi' },
    { name: 'Truist', src: '/img/trust-logo.svg', alt: 'Логотип Truist' },
    { name: 'GS', src: '/img/gs-favicon-180 1.svg', alt: 'Логотип GS (Goldman Sachs)' },
    { name: 'Wells Fargo', src: '/img/wells-fargo-logo.svg', alt: 'Логотип Wells Fargo' }
  ];

  // Данные для отзывов
  const testimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      review: 'Debt Agent helped me eliminate over $45,000 in credit card debt. Their team was professional, supportive, and guided me through every step. I\'m now debt-free and couldn\'t be happier!',
      date: 'January 15, 2025'
    },
    {
      id: 2,
      name: 'Michael Chen',
      review: 'I was drowning in debt from multiple credit cards. Debt Agent negotiated with my creditors and reduced my total debt by 60%. The process was smooth and stress-free. Highly recommended!',
      date: 'December 28, 2024'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      review: 'After losing my job, I thought bankruptcy was my only option. Debt Agent showed me a better way. They helped me settle my debts for less than half of what I owed. Thank you so much!',
      date: 'January 8, 2025'
    },
    {
      id: 4,
      name: 'David Thompson',
      review: 'Working with Debt Agent was the best financial decision I\'ve ever made. They reduced my monthly payments significantly and helped me become debt-free in just 3 years. Excellent service!',
      date: 'January 22, 2025'
    }
  ];

  // Данные для преимуществ
  const features = [
    {
      id: 1,
      icon: <Globe size={48} />,
      title: 'Global Partnership',
      description: 'We collaborate with major creditors and financial institutions across the United States to negotiate the best possible debt relief solutions for our clients.',
      imageSrc: world_icon__features,
      imageAlt: 'Global partnership icon - world globe'
    },
    {
      id: 2,
      icon: <Users size={48} />,
      title: 'Trusted by Thousands',
      description: 'Over 50,000 satisfied clients have achieved financial freedom through our services. Join our community of success stories and start your debt-free journey today.',
      imageSrc: warranty,
      imageAlt: 'Client reviews and warranty icon'
    },
    {
      id: 3,
      icon: <Award size={48} />,
      title: 'Proven Track Record',
      description: 'Since 2001, we have successfully helped Americans eliminate over $2 billion in debt. Our reputation is built on results, transparency, and exceptional client service.',
      imageSrc: reputation_icon__features,
      imageAlt: 'Reputation and awards icon'
    }
  ];



  // Компонент ImagePlaceholder
  const ImagePlaceholder = ({ src, alt, width, height, placeholderText = 'Image', style = {} }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const placeholderUrl = `https://placehold.co/${width}x${height}?text=${encodeURIComponent(placeholderText)}`;
    const imageSrc = (!src || imageError) ? placeholderUrl : src;

    return (
      <img
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        style={{
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          objectFit: 'cover',
          transition: 'opacity 0.3s ease-in-out',
          opacity: imageLoaded ? 1 : 0.8,
          ...style
        }}
        onError={() => setImageError(true)}
        onLoad={() => setImageLoaded(true)}
        loading="lazy"
      />
    );
  };

  // Компонент карточки отзыва
  const TestimonialCard = ({ name, review, date }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div 
        className={`testimonial-card ${isHovered ? 'hovered' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="card-header">
          <h3 className="card-name">{name}</h3>
          <div className="stars-container">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={isMobile ? 16 : 20} fill="#ffd700" color="#ffd700" />
            ))}
          </div>
        </div>
        <p className="card-text">{review}</p>
        <p className="card-date">{date}</p>
      </div>
    );
  };

  // Компонент карточки преимущества
  const FeatureCard = ({ icon, title, description, imageSrc, imageAlt }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div 
        className={`feature-card ${isHovered ? 'hovered' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {imageSrc ? (
          <img src={imageSrc} alt={imageAlt} className="feature-image" />
        ) : (
          <div className="feature-icon">{icon}</div>
        )}
        <h3 className="feature-title">{title}</h3>
        <p className="feature-description">{description}</p>
      </div>
    );
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <Header sections={sections} onJoinClick={openModal} />

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-container">
          <div className="hero-content">
            <p className="hero-small-text">
              I wish to get rid of the extra credit cards and not pay the bills!
            </p>
            <h1 className="hero-title">
              Your debts are closed. <span className="green-text">Success!</span>
            </h1>
            <p className="hero-description">
              Achieve total financial freedom with our 100% free legal services. Your new chapter starts today.
            </p>
            <button className="cta-button" onClick={openModal}>
              REFUND THE MONEY
            </button>
          </div>
          <div className="hero-image-container">
            <ImagePlaceholder
              src={hero_banner}
              width={600}
              height={700}
              placeholderText="Hero Image"
            />
          </div>
        </div>
      </section>

      {/* Bank Logos Section
      <section id="banks" className="bank-logos-section">
        <div className="bank-background-circle"></div>
        <div className="logos-container">
          {bankLogos.map((bank) => (
            <div key={bank.name} className="logo-item">
              <ImagePlaceholder
                src={bank.src}
                alt={bank.alt}
                width={120}
                height={60}
                placeholderText={bank.name}
              />
            </div>
          ))}
        </div>
      </section> */}

      {/* Testimonial Badge Section */}
      <section id="testimonial" className="testimonial-badge-section">
        {/* Двойной градиентный фон */}
        <div className="testimonial-gradient-bg">
          <div className="gradient-circle gradient-circle-1"></div>
          <div className="gradient-circle gradient-circle-2"></div>
        </div>
        
        <div className="testimonial-container">
          {/* Левая часть с контентом */}
          <div className="testimonial-content-wrapper">
            <h2 className="testimonial-main-title">
              We've been helping people with debts since 2001
            </h2>
            
            <div className="testimonial-quote-block">
              <span className="quote-mark quote-open">"</span>
              <p className="testimonial-quote-text">
                We were born to do things differently. We don't just see cases; we see people dreaming of a second chance. 
                That's why we created a 100% free legal service that combines expert negotiation with real human support. 
                How is it possible? Get to know our mission.
              </p>
              <span className="quote-mark quote-close">"</span>
            </div>
            
            <button className="testimonial-cta-button" onClick={openModal}>
              <span>Start My Success Story</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          
          {/* Правая часть с иллюстрацией */}
          <div className="testimonial-image-wrapper">
            {/* Декоративные элементы */}
            <div className="decorative-element deco-top-left"></div>
            <div className="decorative-element deco-top-right"></div>
            <div className="decorative-element deco-bottom-left"></div>
            <div className="decorative-element deco-bottom-right"></div>
            
            {/* Основное изображение */}
            <div className="testimonial-image-container">
              <img
                src={hero_quote_section}
                alt="Успешный бизнесмен с телефоном"
                className="testimonial-main-image"
              />
            </div>
            
            {/* Информационная карточка сверху */}
            <div className="info-card info-card-top">
              <p>Achieve total financial freedom with our 100% free legal services. Your new chapter starts today.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="final-cta" className="final-cta-section" style={{ backgroundImage: `url(${hero_phone_app_banner})` }}>
        {/* Темный оверлей для читаемости текста */}
        <div className="final-overlay"></div>
        
        <div className="final-simple-container">
          {/* Левая часть с текстом */}
          <div className="final-content-left">
            <h2 className="final-simple-title">
              We're Here to Make Your Life Easier!
            </h2>
            
            <button className="final-simple-btn" onClick={openModal}>
              Reduce my Costs
            </button>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section id="success-stories" className="success-stories-section">
        <div className="success-background-circle"></div>
        <h2 className="success-title">See Our Success Stories.</h2>
        <div className="cards-grid">
          {testimonials.map(testimonial => (
            <TestimonialCard 
              key={testimonial.id}
              name={testimonial.name}
              review={testimonial.review}
              date={testimonial.date}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="features-background-circle"></div>
        <div className="features-content-wrapper">
          <h2 className="features-title">Our Features and Advantages</h2>
          <div className="features-grid">
            {features.map(feature => (
              <FeatureCard
                key={feature.id}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                imageSrc={feature.imageSrc}
                imageAlt={feature.imageAlt}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Get Legal Help Section - New Design */}
      <section id="legal-help" className="legal-help-section">
        {/* Два градиентных круга на фоне */}
        <div className="legal-help-gradient-bg">
          <div className="legal-help-gradient-circle legal-help-gradient-circle-1"></div>
          <div className="legal-help-gradient-circle legal-help-gradient-circle-2"></div>
        </div>
        
        <div className="legal-help-container">
          {/* Левая часть с контентом */}
          <div className="legal-help-content">
            <h2 className="legal-help-title">
              Get Legal Help in Minutes —<br /> Just Fill Out the Form!
            </h2>
            
            <p className="legal-help-description">
              Join our support program. Our legal experts specialize in negotiating with major creditors, including Capital One, to significantly reduce or even completely forgive what you owe.
            </p>
            
            <button className="legal-help-btn" onClick={openModal}>
              Reduce my Costs
            </button>
          </div>
          
          {/* Правая часть с телефоном */}
          <div className="legal-help-image">
            <ImagePlaceholder
              src={phone_app_banner}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Modal Window */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Are you sure you want to continue?</h3>
            <div className="modal-buttons">
              <button className="modal-confirm-button" onClick={() => { handleConfirm(); closeModal(); }}>
                Yes
              </button>
              <button className="modal-cancel-button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
